import React, { useState } from "react";
import { Card, Form, Button, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { MoveLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./SocialListingAgent.scss";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { formatNumber } from "src/utils/numberFormat";

interface CompetitorFormRow {
  brandName?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  redditSubreddits?: string;
}

const CompetitiveAnalysisForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [competitors, setCompetitors] = useState([{ id: 1 }]);
  const maxCompetitors = 5;
  const navigate = useNavigate();
  const localizedMaxCompetitors = formatNumber(maxCompetitors, {}, i18n.language);

  const addCompetitor = () => {
    if (competitors.length >= maxCompetitors) {
      return;
    }
    setCompetitors(prev => [...prev, { id: prev.length + 1 }]);
  };

  const onFinish = (values: { competitors?: CompetitorFormRow[] }) => {
    const rows = values?.competitors;
    if (!rows?.length) {return;}

    navigate("/account/social-listing-agent/loader", {
      state: {
        analysisType: "competitive_analysis" as const,
        competitors: rows,
      },
    });
  };

  return (
    <div className="competitive-form-container">
      <div className="competitive-form-header">
        <Button
          className="competitive-back-button"
          onClick={() => navigate("/account/social-listing-agent")}
        >
          <MoveLeft className="Brand-back-button" />
          {t("socialListingAgent.competitiveForm.back")}
        </Button>
      </div>

      <div className="competitive-form-title-row">
        <h1 className="competitive-form-title">
          {t("socialListingAgent.competitiveForm.title")}
        </h1>
        <Tooltip
          title={t("socialListingAgent.competitiveForm.maxCompetitorsTooltip", {
            count: localizedMaxCompetitors,
          })}
          placement="top"
        >
          <span className="competitive-info-icon" tabIndex={0} role="img" aria-label="Info">
            ?
          </span>
        </Tooltip>
      </div>

      <p className="competitive-form-subtitle">
        {t("socialListingAgent.competitiveForm.subtitle")}
      </p>

      <Form layout="vertical" onFinish={onFinish} className="competitive-form">
        <div className="competitor-grid">
          {competitors.map((comp, index) => (
            <Card key={comp.id} className="competitive-form-card" bordered={false}>
              <h3>
                {t("socialListingAgent.competitiveForm.competitorTitle", {
                  index: formatNumber(index + 1, {}, i18n.language),
                })}
              </h3>

              <Form.Item
                label={t("socialListingAgent.competitiveForm.brandNameLabel")}
                className="brand-form-label"
                name={["competitors", index, "brandName"]}
                rules={[
                  {
                    required: true,
                    message: t("socialListingAgent.competitiveForm.brandNameRequired"),
                  },
                ]}
              >
                <StyledTextInput
                  placeholder={t("socialListingAgent.competitiveForm.brandNamePlaceholder")}
                  className="brand-placeholder"
                />
              </Form.Item>

              <Form.Item
                label={t("socialListingAgent.competitiveForm.instagramHandleLabel")}
                className="brand-form-label"
                name={["competitors", index, "instagram"]}
              >
                <StyledTextInput placeholder="@username" className="brand-placeholder" />
              </Form.Item>

              <Form.Item
                label={t("socialListingAgent.competitiveForm.facebookPageIdLabel")}
                className="brand-form-label"
                name={["competitors", index, "facebook"]}
              >
                <StyledTextInput
                  placeholder={t("socialListingAgent.competitiveForm.facebookPageIdPlaceholder")}
                  className="brand-placeholder"
                />
              </Form.Item>

              <Form.Item
                label={t("socialListingAgent.competitiveForm.tiktokUsernameLabel")}
                className="brand-form-label"
                name={["competitors", index, "tiktok"]}
              >
                <StyledTextInput placeholder="@username" className="brand-placeholder" />
              </Form.Item>

              <Form.Item
                label={t("socialListingAgent.competitiveForm.redditSubredditsLabel")}
                className="brand-form-label"
                name={["competitors", index, "redditSubreddits"]}
              >
                <StyledTextInput
                  placeholder={t("socialListingAgent.competitiveForm.redditSubredditsPlaceholder")}
                  className="brand-placeholder"
                />
              </Form.Item>

              <p className="competitor-note">
                {t("socialListingAgent.competitiveForm.socialHandlesOptional")}
              </p>
            </Card>
          ))}
        </div>

        <div className="competitive-actions">
          <Button
            type="dashed"
            className="add-competitor-box"
            onClick={addCompetitor}
            disabled={competitors.length >= maxCompetitors}
          >
            <PlusOutlined /> {t("socialListingAgent.competitiveForm.addCompetitor")}
          </Button>

          <Button type="primary" htmlType="submit" className="button button--primary">
            {t("socialListingAgent.competitiveForm.startCompetitorAnalysis")}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CompetitiveAnalysisForm;
